import { useCallback, useEffect, useRef, useState } from 'react'
import { InputGroup } from 'react-bootstrap'
import { toast } from 'react-toastify'
import ToastMessage from '../ToastMessage'
import Button from '../Button'
import Modal from '../Modal'
import { BalanceText, Input } from './Styled'
import { useTokenBalance, useActiveWeb3React, useTokenContract } from '../../hooks'
import { formatNumber, toWei } from '../../utils'
import config from '../../config.json'

interface IUnstakeModalProps {
  show: boolean
  onHide: () => void
  tokenName: string
  poolId: number
  version: number
  onRefresh: () => void
}

function UnstakeModal(props: IUnstakeModalProps): JSX.Element {
  const { show, onHide, tokenName, poolId, version, onRefresh } = props
  const [isLoading, setLoading] = useState(false)
  const [amount, setAmount] = useState(0)
  const [strAmount, setStrAmount] = useState('0')
  const [stakedBalance, setStakedBalance] = useState(0)

  const { account, chainId } = useActiveWeb3React()
  const networkId = chainId ?? Number(process.env.REACT_APP_CHAIN_ID)
  const pool = config.pools[networkId].find(p => p.id === poolId)
  // @ts-ignore
  const stakedBalanceCallback = useTokenBalance(pool?.contract, account)
  const stakingCallback = useTokenContract(pool?.contract)

  const ref = useRef()

  useEffect(() => {
    const fetchData = async () => {
      const _stakedBalance = await stakedBalanceCallback()
      setStakedBalance(_stakedBalance)
    }

    fetchData()

    if (ref.current) {
      // @ts-ignore
      ref.current.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  const closeModal = () => {
    setAmount(0)
    setStrAmount('0')
    onHide()
  }

  const onConfirm = async () => {
    try {
      setLoading(true)
      let receipt: any = {}

      if (stakingCallback) {
        receipt = await stakingCallback.methods.withdraw(toWei(amount).toString(10)).send({ from: account })
      }

      if (receipt) {
        toast.success(
          <ToastMessage
            color="success"
            headerText="Success!"
            bodyText="Unstake successful"
            link={`${process.env.REACT_APP_EXPLORER_URL}/tx/${receipt.transactionHash}`}
            linkText="View Transaction"
          />,
          {
            toastId: 'onConfirm',
          },
        )

        await onRefresh()
        closeModal()
      }
    } catch (error: any) {
      // we only care if the error is something _other_ than the user rejected the tx
      if (error?.code !== 4001) {
        toast.error(
          <ToastMessage color="error" headerText="Error!" bodyText="Could not unstake. Please try again." />,
          {
            toastId: 'onConfirm',
          },
        )
      }
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const onAmountChange = (e: any) => {
    const { value } = e.currentTarget
    setStrAmount(value)
    setAmount(Number(value) || 0)
  }

  const onBlur = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    e => {
      setStrAmount(amount.toString())
    },
    [amount],
  )

  const onFocus = (e: any) => {
    e.target.select()
  }

  const onKeyUp = (e: any) => {
    if (e.keyCode === 13) {
      onConfirm()
    }
  }

  const onMax = () => {
    setAmount(stakedBalance)
    setStrAmount(stakedBalance.toString())
  }

  return (
    <Modal
      show={show}
      onHide={closeModal}
      title={`Unstake ${tokenName}`}
      button={
        <Button onClick={onConfirm} disabled={isLoading} loading={isLoading}>
          Confirm
        </Button>
      }
    >
      {version === 2 && <p>After unstake, you will be able to withdraw your token for 24 hours.</p>}
      <BalanceText>
        Available: {formatNumber(stakedBalance.toFixed(3))} {tokenName}
      </BalanceText>
      <InputGroup>
        <Input
          ref={ref}
          type="number"
          placeholder="0"
          min="0"
          value={strAmount}
          onChange={onAmountChange}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyUp={onKeyUp}
        />
        <Button color="secondary" onClick={onMax}>
          Max
        </Button>
      </InputGroup>
    </Modal>
  )
}

export default UnstakeModal
